import { IUpdateValue, parseText, IFormData } from './BusinessDataFormUtils';
import { isArray } from '../../../../../../../Utils/ramdaCookbook';
import Logger from '../../../../../../../Utils/Logger';
import { getVersionAndId } from '../../../../../../../Utils/dataUtils';
import {
  assocPath,
  path,
  Path,
  mergeDeepWith,
  type,
  append,
  concat,
} from 'ramda';
import { mergeBusinessDataMutations } from './MergeBusinessDataMutations';

export enum MutationOperation {
  Ref = 'Ref',
  Values = 'Values',
  Add_Refs = 'Add_Refs',
  Delete_Refs = 'Delete_Refs',
  Add_Values = 'Add_Values',
  Update_Values = 'Update_Values',
  Delete_Values = 'Delete_Values',
  Undelete = 'Undelete',
}

interface IValues {
  [key: string]: any;
}

interface IOwnFields {
  operation: 'OwnFields';
  values: IValues;
}
interface IAssociationOneRef {
  operation: 'Ref';
  associationName: string;
  id?: string | null;
  values?: IValues | null;
}
interface IAssociationOneValues {
  operation: 'Values';
  associationName: string;
  values?: IValues;
}
interface IAssociationManyAddRefs {
  operation: 'Add_Refs';
  associationName: string;
  id: string;
  values?: IValues;
}
interface IAssociationManyDeleteRefs {
  operation: 'Delete_Refs';
  associationName: string;
  id: string;
}
interface IAssociationManyAddValues {
  operation: 'Add_Values';
  associationName: string;
  id: string;
  values?: IValues;
}
interface IAssociationManyUpdateValues {
  operation: 'Update_Values';
  associationName: string;
  id: string;
  values?: IValues;
}
interface IAssociationManyDeleteValues {
  operation: 'Delete_Values';
  associationName: string;
  id: string | string[];
}
interface IAssociationManyUndelete {
  operation: 'Undelete';
  associationName: string;
  id: string;
  values?: IValues;
}

type IAssociatonObject =
  | IOwnFields
  | IAssociationOneRef
  | IAssociationOneValues
  | IAssociationManyAddRefs
  | IAssociationManyDeleteRefs
  | IAssociationManyAddValues
  | IAssociationManyUpdateValues
  | IAssociationManyDeleteValues
  | IAssociationManyUndelete;

export type FormUpdateAssociatonType = string | IAssociatonObject;

const mergeBusinessUpdateValues = (leftValue: any, rightValue: any): any =>
  mergeDeepWith(
    (leftValue: any, rightValue: any) => {
      if (type(leftValue) === 'Array' && type(rightValue) === 'Array') {
        return [mergeBusinessUpdateValues(leftValue[0], rightValue[0])];
      } else {
        return rightValue;
      }
    },
    leftValue,
    rightValue
  );

const getUpdateValueFromUpdateArray = (
  updateArray: FormUpdateAssociatonType[],
  data: IFormData
): IUpdateValue => {
  let currentDataPath: Path = [];
  let currentUpdateValuePath: Path = [];

  const updateValue = (updateArray as FormUpdateAssociatonType[]).reduce(
    (previousUpdateValue, update) => {
      let newUpdateValues = {
        ...getVersionAndId(data, [], undefined),
      };
      if (typeof update === 'string') {
        currentDataPath.push('_Associations', update);
        currentUpdateValuePath.push('_Associations', `${update}_Values`);
        newUpdateValues = assocPath(
          currentUpdateValuePath,
          { ...getVersionAndId(data, currentDataPath, undefined) },
          newUpdateValues
        );
      } else {
        switch (update.operation) {
          case 'OwnFields': {
            newUpdateValues = {
              id: data.id,
              _version: data._version,
              ...update.values,
            };
            break;
          }
          case 'Ref': {
            currentDataPath.push('_Associations', update.associationName);
            currentUpdateValuePath.push(
              '_Associations',
              `${update.associationName}_Ref`
            );
            newUpdateValues = assocPath(
              currentUpdateValuePath,
              update.values === undefined
                ? update.id
                : update.values === null
                ? null
                : {
                    id: update.id,
                    ...update.values,
                  },
              newUpdateValues
            );
            break;
          }
          case 'Values': {
            currentDataPath.push('_Associations', update.associationName);

            const versionAndId = getVersionAndId(
              data,
              currentDataPath,
              undefined
            );
            currentUpdateValuePath.push(
              '_Associations',
              `${update.associationName}_Values`
            );
            newUpdateValues = assocPath(
              currentUpdateValuePath,
              versionAndId.id
                ? {
                    ...versionAndId,
                    ...update.values,
                  }
                : update.values,
              newUpdateValues
            );
            break;
          }
          case 'Add_Refs': {
            currentUpdateValuePath.push(
              '_Associations',
              `${update.associationName}_Add_Refs`,
              0
            );
            newUpdateValues = assocPath(
              currentUpdateValuePath,
              {
                id: update.id,
                ...update.values,
              },
              newUpdateValues
            );
            break;
          }
          case 'Add_Values': {
            currentUpdateValuePath.push(
              '_Associations',
              `${update.associationName}_Add_Values`,
              0
            );
            newUpdateValues = assocPath(
              currentUpdateValuePath,
              {
                id: update.id,
                ...update.values,
              },
              newUpdateValues
            );
            break;
          }
          case 'Delete_Refs': {
            currentUpdateValuePath.push(
              '_Associations',
              `${update.associationName}_Delete_Refs`,
              0
            );
            newUpdateValues = assocPath(
              currentUpdateValuePath,

              update.id,

              newUpdateValues
            );
            break;
          }
          case 'Delete_Values': {
            currentDataPath.push('_Associations', update.associationName);

            const dataList = path<IUpdateValue[]>(currentDataPath, data);

            const ids: string[] = Array.isArray(update.id)
              ? update.id
              : [update.id];

            ids.forEach((id, idsIndex) => {
              const index = dataList?.findIndex(dataItem => dataItem.id === id);

              let dataDeleteElementPath = currentDataPath;
              if (index != null && index !== -1) {
                dataDeleteElementPath = append(index, currentDataPath);
              } else {
                // TODO: Vad händer här?
              }

              const updateValueDeleteElementPath = concat(
                currentUpdateValuePath,
                [
                  '_Associations',
                  `${update.associationName}_Delete_Values`,
                  idsIndex,
                ]
              );

              newUpdateValues = assocPath(
                updateValueDeleteElementPath,
                {
                  ...getVersionAndId(data, dataDeleteElementPath, id),
                },
                newUpdateValues
              );
            });

            break;
          }
          case 'Update_Values': {
            currentDataPath.push('_Associations', update.associationName);

            const dataList = path<IUpdateValue[]>(currentDataPath, data);
            const index = dataList?.findIndex(
              dataItem => dataItem.id === update.id
            );

            if (index != null && index !== -1) {
              currentDataPath.push(index);
            } else {
              // TODO: Vad händer här?
            }

            currentUpdateValuePath.push(
              '_Associations',
              `${update.associationName}_Update_Values`,
              0
            );

            newUpdateValues = assocPath(
              currentUpdateValuePath,
              {
                ...getVersionAndId(data, currentDataPath, update.id),
                ...update.values,
              },
              newUpdateValues
            );
            break;
          }
          case 'Undelete': {
            currentDataPath.push('_Associations', update.associationName);

            const dataList = path<IUpdateValue[]>(currentDataPath, data);
            const index = dataList?.findIndex(
              dataItem => dataItem.id === update.id
            );

            if (index != null && index !== -1) {
              currentDataPath.push(index);
            } else {
              // TODO: Vad händer här?
            }

            currentUpdateValuePath.push(
              '_Associations',
              `${update.associationName}_Undelete`,
              0
            );

            newUpdateValues = assocPath(
              currentUpdateValuePath,
              {
                ...getVersionAndId(data, currentDataPath, update.id),
                ...update.values,
              },
              newUpdateValues
            );

            break;
          }
          default: {
            Logger.error(
              `getFormUpdateValue: Unknown operation in update: ${update}`
            );
            break;
          }
        }
      }

      return mergeBusinessUpdateValues(previousUpdateValue, newUpdateValues);
    },
    {}
  );

  return updateValue;
};

/** Omvandlar en updateArray till en IUpdateValue som kan skickas in till ett formulär med en UPDATE action */
export const getFormUpdateValue = (
  updateArray: FormUpdateAssociatonType[] | Array<FormUpdateAssociatonType[]>,
  data: IUpdateValue
): IUpdateValue => {
  const parsedData = parseText(data);

  let updateValue = {};
  if (updateArray[0]) {
    if (!isArray(updateArray[0])) {
      updateValue = getUpdateValueFromUpdateArray(
        updateArray as FormUpdateAssociatonType[],
        parsedData
      );
    } else if (isArray(updateArray[0])) {
      (updateArray as Array<FormUpdateAssociatonType[]>).forEach(
        updateArrayItem => {
          updateValue = mergeBusinessDataMutations(
            updateValue,
            getUpdateValueFromUpdateArray(
              updateArrayItem as FormUpdateAssociatonType[],
              parsedData
            )
          );
        }
      );
    }
  }

  return updateValue;
};
